<script setup lang="ts">
import { computed, type PropType, ref } from 'vue';
import  Dialog from 'primevue/dialog';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';
import ProgressSpinner from 'primevue/progressspinner';
import { FormModel, type GraphTypeModel, GraphType, type FormField, FormFieldType, FIELD_TYPES, type NetworkGraphValues, Graph } from '@/models';
import { useGraphStore } from '@/stores/graph';
import { useFormStore } from '@/stores/forms';
import { GRAPH_TYPES } from '@/utils';
import {
  CustomButton,
  ButtonType
} from '@/components';

const formStore = useFormStore();
const graphStore = useGraphStore();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  existingGraph: {
    type: Object as PropType<Graph>,
    required: false
  }
});

const emits = defineEmits(['onClose']);

const closeDialog = () => {
  emits('onClose');
  clearForm();
};

const selectedType = ref<GraphTypeModel>();
const selectedForm = ref<FormModel>();
const selectedField = ref<FormField>();
const timePeriod = ref(false);
const selectedDates = ref();

const setGraphData = () => {
  if (props.existingGraph) {
    selectedType.value = GRAPH_TYPES.find(field => field.type ===  props.existingGraph!.chartType);
    (async () => {
      await formStore.getForm(props.existingGraph!.formIdentifier);
      selectedForm.value = formStore.currentForm;
      selectedField.value = selectedForm.value?.items?.find(item => item.identifier === props.existingGraph?.fieldIdentifier);
    })();
    
    timePeriod.value = props.existingGraph.dateType === 'selection';
  }
};

setGraphData();

const clearForm = () => {
  selectedType.value = undefined;
  selectedForm.value = undefined;
  selectedField.value = undefined;
  timePeriod.value = false;
  selectedDates.value = undefined;
};

const handleFormSelection = () => {
  if (selectedForm.value) {
    formStore.getForm(selectedForm.value?.identifier);
  }
};

const formFields = computed(() => {
  const fields: FormField[] = [];
  formStore.currentForm?.items?.forEach(formItem => {
        
    switch (selectedType.value?.type) {
      case GraphType.LINE_CHOICE:
      case GraphType.PIE:
      case GraphType.BAR:
        if (formItem.type === FormFieldType.CHOICE || formItem.type === FormFieldType.DROPDOWN) {
          formItem.icon = getFieldIcon(formItem.type);
          fields.push(formItem);
        }
        break;
        
      case GraphType.BASIC:
      case GraphType.LINE:
        if (formItem.type === FormFieldType.NUMBER) {
          formItem.icon = getFieldIcon(formItem.type);
          fields.push(formItem);
        }
        break;
      default:
        break;
    }
  });
  return fields;
});

const getFieldIcon = (type: FormFieldType) => {
  const fieldType = FIELD_TYPES.find(field => field.type === type);
  return {
    style: fieldType!.iconStyle,
    icon: fieldType!.icon
  };
};

const enableCreate = computed(() => {
  return (selectedType.value?.type === GraphType.FREQUENCY && selectedForm.value) ||
  (selectedType.value && selectedForm.value && selectedField.value ? (timePeriod.value ? selectedDates.value : true) : false);
});

const createGraph = async () => {
  const postGraph: NetworkGraphValues = {
    survey: selectedForm.value!.identifier,
    field: selectedField.value?.identifier,
    chartType: selectedType.value!.type,
    investigation: '', // will be assinged in the store
    dateType: timePeriod.value ? 'selection' : 'none',
    dateSelectionStart: timePeriod.value && selectedDates.value ? selectedDates.value[0] : undefined,
    dateSelectionEnd: timePeriod.value && selectedDates.value ? selectedDates.value[1] : undefined
  };
  await graphStore.createGraph(postGraph);
  closeDialog();
};

</script>

<template>
    <Dialog :visible="visible"
        modal
        header="Create graph"
        class="dialog"
        style="width: 500px;"
        @update:visible="closeDialog">
        <div class="new-graph-wrapper">
            <div class="chart-group type">
                <div class="group-header">Chart type</div>
                <div class="p-dropdown-wrapper">
                    <Select v-model="selectedType"
                        placeholder="Select a type"
                        :options="GRAPH_TYPES">
                        <template #value="slotProps">
                            <div v-if="slotProps.value" class="type-item">
                                <div class="icon">
                                    <FontAwesomeIcon :icon="[`${slotProps.value.icon.style}`, `${slotProps.value.icon.icon}`]" />
                                </div>
                                <div>{{ slotProps.value.label }}</div>
                            </div>
                            <span v-else>
                                {{ slotProps.placeholder }}
                            </span>
                        </template>
                        <template #option="slotProps">
                            <div class="type-item">
                                <div class="icon">
                                    <FontAwesomeIcon :icon="[`${slotProps.option.icon.style}`, `${slotProps.option.icon.icon}`]" />
                                </div>
                                <div class="label">{{ slotProps.option.label }}</div>
                            </div>
                        </template>
                    </Select>
                </div>
            </div>
            <div class="chart-group form">
                <div class="group-header">Form</div>
                <div class="form-group">
                    <div class="p-dropdown-wrapper">
                        <Select v-model="selectedForm"
                            placeholder="Select a form"
                            @change="handleFormSelection"
                            :options="formStore.formList">
                            <template #value="slotProps">
                                <div v-if="slotProps.value" class="type-item">
                                    <div class="icon">
                                        <FontAwesomeIcon :icon="[`fas`, `file`]" />
                                    </div>
                                    <div>{{ slotProps.value.title }}</div>
                                </div>
                                <span v-else>
                                    {{ slotProps.placeholder }}
                                </span>
                            </template>
                            <template #option="slotProps">
                                <div class="type-item">
                                    <div class="icon">
                                        <FontAwesomeIcon :icon="[`far`, `file`]" />
                                    </div>
                                    <div class="label">{{ slotProps.option.title }}</div>
                                </div>
                            </template>
                        </Select>
                    </div>
                    <ProgressSpinner strokeWidth="4" class="progress" v-if="formStore.isGettingForms.isLoading"/>
                </div>
            </div>
            <div class="chart-group fields" v-if="formFields.length > 0">
                <div class="group-header">Field</div>
                <div class="p-dropdown-wrapper">
                    <Select v-model="selectedField"
                        placeholder="Select a field"
                        @change="handleFormSelection"
                        :options="formFields">
                        <template #value="slotProps">
                            <div v-if="slotProps.value" class="type-item">
                                <div class="icon">
                                    <FontAwesomeIcon :icon="[`${slotProps.value.icon.style}`, `${slotProps.value.icon.icon}`]"  />
                                </div>
                                <div>{{ slotProps.value.label }}</div>
                            </div>
                            <span v-else>
                                {{ slotProps.placeholder }}
                            </span>
                        </template>
                        <template #option="slotProps">
                            <div class="type-item">
                                <div class="icon">
                                    <FontAwesomeIcon :icon="[`${slotProps.option.icon.style}`, `${slotProps.option.icon.icon}`]"  />
                                </div>
                                <div class="label">{{ slotProps.option.label }}</div>
                            </div>
                        </template>
                    </Select>
                </div>
            </div>
            <div class="form-field-error" v-if="!enableCreate && selectedForm && formFields.length === 0">
                No applicable fields found for this form and chart type. Try another!
            </div>
            <div class="chart-group time" v-if="selectedField">
                <div class="time-period">
                    <Checkbox v-model="timePeriod"
                        inputId="time-period"
                        name="time"
                        class="time-check"
                        :binary="true"/>
                    <div class="group-header">Time Period</div>
                </div>
                <div class="date-filters p-dropdown-wrapper" v-if="timePeriod">
                    <DatePicker v-model="selectedDates"
                        selectionMode="range"
                        :manualInput="false"
                        showIcon/>
                </div>
            </div>

            <div class="footer">
                <div class="chart-buttons">
                    <CustomButton text="Cancel"
                        :type="ButtonType.OUTLINED"
                        @onClick="closeDialog"/>
                    <CustomButton text="Create"
                        :type="ButtonType.POSITIVE"
                        :marginLeft="16"
                        :disabled="!enableCreate"
                        @onClick="createGraph"
                        :icon="{ icon: 'chart-simple', style: 'fal' }"/>
                </div>
            </div>
        </div>

        <div class="root-progress" v-if="graphStore.createGraphsLoader.isLoading">
            <ProgressSpinner strokeWidth="4"/>
        </div>
    </Dialog>
</template>

<style lang="scss" scoped>
:deep(.p-progressspinner) {
    margin: unset;
}
.root-progress {
    display: flex;
    align-items: center;
    justify-content: center;

    min-height: 500px;
}
.chart-group {
    margin-bottom: 16px;

    .group-header {
        font-weight: 500;
    }

    .form-group {
        display: flex;
        align-items: center;

        .progress {
            margin-top: 12px;
            height: 32px;
            width: 32px;
        }
    }
}
.p-dropdown-wrapper{
    padding-top: 12px;
    padding-right: 24px;
}
.type-item {
    display: flex;
    align-items: center;

    .icon {
        margin-right: 16px;
        font-size: 16px;
    }
}
.time-period {
    display: flex;
    align-items: center;
    padding-top: 8px;

    .time-check {
        margin-right: 8px;
    }
}
.footer {
    display: flex;
    justify-content: flex-end;
}
.chart-buttons {
    display: flex;
}
</style>
